import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {Button, Dialog} from '@betareader/design-system';
import Icon from '../../components/AppIcon';
import { useTranslation } from 'react-i18next';
import {useDispatch, useSelector} from '../../reduxHooks';
import styled from 'styled-components';

import BookItem from '../../components/book/bookItem';
import BookItemPlaceholder from '../../components/book/bookItemPlaceholder';
import ChapterUploadModal from '../content/chapter/upload/chapterUploadModal';
import CreateFirstBookSegment from './createFirstBookSegment';
import ActiveReaders from '../../components/statistics/activeReaders';
import WelcomeMessage from '../../components/standard-messages/welcomeMessage';
import GetMoreReadersMessage from '../../components/standard-messages/getMoreReadersMessage';
import UnlockSignupBonusMessage from '../../components/standard-messages/unlockSignupBonusMessage';
import NewBook from '../book/newBook';
import SampleChaptersTab from '../sampleChapters/sampleChaptersTab';

import { updateWritingList, updateCollaborationList } from '../../modules/book';
import { fetchReaderStatistics } from '../../modules/reader';

import privacy from '../../config/privacy';
import { getTheme } from '../../constants/theme';
import { authUtil, productCapUtil, uxAnalyticsUtil } from '../../utils';

const READER_STATS_INTERVAL = 60000;

const Dashboard = styled.div`
  box-sizing: border-box;
  width: 100%;
  --library-surface: ${({ theme }) =>
    (theme.navigation || getTheme('bright').navigation).surface};
  --library-border: ${({ theme }) =>
    (theme.navigation || getTheme('bright').navigation).border};
  --library-text: ${({ theme }) =>
    (theme.navigation || getTheme('bright').navigation).heading};
  --library-muted: ${({ theme }) =>
    (theme.navigation || getTheme('bright').navigation).text};
  --library-accent: ${({ theme }) =>
    (theme.navigation || getTheme('bright').navigation).accent};
  --library-focus: ${({ theme }) =>
    (theme.navigation || getTheme('bright').navigation).focus};
  padding: 0 24px 24px;
  max-width: 1280px;
  margin: 0 auto;
  min-width: 0;

  .dashboard-toolbar .br-button--secondary {
    color: var(--library-text);
    background: var(--library-surface);
    box-shadow: inset 0 0 0 1px var(--library-border);
  }
  .dashboard-toolbar {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;
  }
  #books-length-label {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  #books-length-label .book-count-badge {
    margin: 0;
    background: var(--library-surface);
    color: var(--library-muted) !important;
  }
  .dashboard-tabs {
    display: flex;
    border-bottom: 1px solid var(--library-border);
    margin-bottom: 8px;
  }
  .dashboard-tab {
    min-height: 48px;
    flex: 1;
    justify-content: center;
    border: 0;
    border-bottom: 2px solid transparent;
    background: transparent;
    color: var(--library-muted);
    font: inherit;
    cursor: pointer;
  }
  .dashboard-tab[aria-selected='true'] {
    color: var(--library-accent);
    border-color: var(--library-accent);
  }
  .book-list {
    margin: 0;
  }
  .book-list > .manuscript-book-item {
    padding: 24px 0;
    gap: 20px;
    border-color: var(--library-border);
  }
  .book-list > .manuscript-book-item > .book-item-content {
    min-width: 0;
    padding: 0;
  }
  .manuscript-book-item .book-item-heading {
    gap: 8px;
    width: 100%;
    color: var(--library-text);
    line-height: 1.35;
  }
  .manuscript-book-item .book-item-reviews {
    flex: 0 0 190px;
    width: 190px;
    align-self: center;
  }
  .book-item-rating {
    color: var(--library-accent);
    background: var(--library-surface);
  }
  .book-item-reviews td:first-child {
    white-space: nowrap;
    padding-right: 12px;
  }
  .manuscript-book-item .book-item-description {
    max-width: 80ch;
  }
  .book-item-title {
    color: inherit !important;
  }
  .book-item-title:hover {
    text-decoration: underline;
  }
  .book-item-title,
  .book-item-reference {
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .manuscript-book-item .book-item-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 8px;
    color: var(--library-muted);
    line-height: 1.5;
  }
  .manuscript-book-item .book-item-description {
    color: var(--library-muted);
    overflow-wrap: anywhere;
  }
  .manuscript-book-item .book-item-metrics {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
  }
  .book-item-metrics .book-item-metric {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 52px;
    min-height: 44px;
    margin: 0;
    background: var(--library-surface);
    color: var(--library-accent);
    border-color: var(--library-border);
  }
  a:focus-visible,
  button:focus-visible,
  .manuscript-book-item summary:focus-visible {
    outline: 2px solid var(--library-focus);
    outline-offset: 3px;
  }
  @media (max-width: 767px) {
    padding: 0 16px 16px;
    .dashboard-toolbar {
      flex-direction: column-reverse;
      align-items: stretch;
      gap: 12px;
      margin-bottom: 16px;
    }
    .dashboard-tab {
      flex: 1 1 0;
      min-width: 0;
      padding: 12px 6px;
      font-size: 12px;
      text-align: center;
      line-height: 1.4;
      overflow-wrap: anywhere;
    }
    .book-list > .manuscript-book-item {
      gap: 0;
      padding: 20px 0;
    }
    .manuscript-book-item .book-item-heading {
      font-size: 18px;
    }
    .manuscript-book-item .book-item-meta {
      font-size: 13px;
      margin-top: 8px;
    }
    .manuscript-book-item .book-item-description {
      font-size: 13px;
      line-height: 1.55;
    }
    .manuscript-book-item .book-item-length {
      flex-basis: 100%;
    }
  }
`;

const DashboardActions = styled.div`
  display: flex;
  flex: 1;
  justify-content: flex-end;
  gap: 8px;

  .br-button {
    min-height: 44px;
    margin: 0;
  }

  @media only screen and (max-width: 767px) {
    flex-wrap: wrap;

    .br-button {
      flex: 1 1 auto;
      padding-right: 12px;
      padding-left: 12px;
    }
  }
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 12px;
`;

export const MyManuscripts = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();

  const refreshDashBoardInterval = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  const user = useSelector(state => state.user.user);
  const activeAccount = useSelector(state => state.account.activeAccount);
  const isFetchingCollaborationsList = useSelector(
    state => state.book.isFetchingCollaborationsList
  );
  const isFetchingWritingList = useSelector(
    state => state.book.isFetchingWritingList
  );
  const userProfile = useSelector(state => state.user.userProfile);
  const writing = useSelector(state => state.book.writing);
  const collaborating = useSelector(state => state.book.collaborating);
  const readerStatistics = useSelector(state => state.reader.readerStatistics);

  const [showChapterUpload, setShowChapterUpload] = useState(false);
  const [showModal, setShowModal] = useState<boolean | string>(false);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    uxAnalyticsUtil.trackPageView(location.pathname);
    const doFetchReaderStatistics = async () => {
      const idToken = await authUtil.getFreshIdToken();
      dispatch(fetchReaderStatistics(idToken));
    };

    doFetchReaderStatistics();
    refreshDashBoardInterval.current = setInterval(
      doFetchReaderStatistics,
      READER_STATS_INTERVAL
    );
    return () => {
      // clear interval when component unmounts
      if (refreshDashBoardInterval.current) clearInterval(refreshDashBoardInterval.current);
    };
  }, []);

  useEffect(() => {
    // set up dashboard again when activeAccount changes
    setupDashboard();
  }, [activeAccount]);

  const setupDashboard = async () => {
    const idToken = await authUtil.getFreshIdToken();
    if (idToken && userProfile) {
      dispatch(
        updateWritingList({
          idToken,
          accountId: activeAccount
            ? activeAccount._id
            : userProfile.account._id,
        })
      );
      dispatch(
        updateCollaborationList({
          idToken,
        })
      );
    }
  };

  const toggleChapterUpload = () => {
    setShowChapterUpload(!showChapterUpload);
    if (!showChapterUpload) {
      uxAnalyticsUtil.trackEvent({
        category: 'Books',
        action: 'clicked-add-book',
        label: 'upload',
      });
    }
  };

  const handleCreateBookClick = () => {
    uxAnalyticsUtil.trackEvent({
      category: 'Books',
      action: 'clicked-add-book',
      label: 'create',
    });
    setShowModal('create-book');
  };

  const renderBooksList = ({
    books = [] as Array<{ _id: string; author?: { _id: string } }>,
    isLoading = false,
    showUpload = false,
  }) => {
    if (isLoading) {
      // Render placeholder if we're still loading
      return [
        <BookItemPlaceholder key="first-placeholder" />,
        <BookItemPlaceholder key="second-placeholder" />,
        <BookItemPlaceholder key="third-placeholder" />,
      ];
    }
    if (books?.length > 0) {
      // Render books if any
      const booksList = books?.map(book => {
        return <BookItem key={book?._id} book={book} />;
      });
      return booksList;
    }
    if (showUpload) {
      // Otherwise, render the upload placeholder to incite the user to start testing a manuscript
      const result = [
        <CreateFirstBookSegment
          key="create-first-book"
          onCreateManuscript={handleCreateBookClick}
        />,
        <WelcomeMessage
          key="welcome"
          toggleChapterUpload={toggleChapterUpload}
        />,
      ];
      if (userProfile?.wasReferred && !userProfile?.signupReferralBonus) {
        result.push(
          <UnlockSignupBonusMessage
            key="unlock-bonus"
            userProfile={userProfile}
            user={user}
            writing={writing}
          />
        );
      }
      return result;
    }
    return null;
  };

  const hideModal = () => setShowModal(false);

  const getCounts = useCallback(
    () => productCapUtil.getBookCounts(writing),
    [writing]
  );

  const { unpublishedCount, privateCount, closedCount, listedCount } =
    getCounts();

  if (!userProfile) {
    return <p role="status">{t('Loading')}</p>;
  }

  const tabLabels = [t('OwnBooks'), t('Collaborations'), t('SampleChapters', 'Sample chapters')];
  const onTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next = index;
    if (event.key === 'ArrowRight') next = (index + 1) % tabLabels.length;
    else if (event.key === 'ArrowLeft') next = (index + tabLabels.length - 1) % tabLabels.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabLabels.length - 1;
    else return;
    event.preventDefault();
    setActiveTab(next);
    const tabs = event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    tabs?.[next]?.focus();
  };

  return (
    <Dashboard>
      {writing && writing?.length > 0 && (
        <ActiveReaders readerStatistics={readerStatistics} />
      )}
      <div>
        <div className="dashboard-toolbar">
          <div id="books-length-label">
            {unpublishedCount > 0 && (
              <CountBadge className="book-count-badge" title={t('PrivacyLevel_state_unpublished')}>
                <Icon name={privacy.privacyIcons.unpublished} />
                {unpublishedCount} {t('PrivacyLevel_state_unpublished')}
              </CountBadge>
            )}
            {privateCount > 0 && (
              <CountBadge className="book-count-badge" title={t('PrivacyLevel_state_private')}>
                <Icon name={privacy.privacyIcons.private} />
                {privateCount} {t('PrivacyLevel_state_private')}
              </CountBadge>
            )}
            {closedCount > 0 && (
              <CountBadge className="book-count-badge" title={t('PrivacyLevel_state_closed')}>
                <Icon name={privacy.privacyIcons.closed} />
                {closedCount} {t('PrivacyLevel_state_closed')}
              </CountBadge>
            )}
            {listedCount > 0 && (
              <CountBadge className="book-count-badge" title={t('PrivacyLevel_state_listed')}>
                <Icon name={privacy.privacyIcons.listed} />
                {listedCount} {t('PrivacyLevel_state_listed')}
              </CountBadge>
            )}
          </div>
          <DashboardActions>
            <Button variant="secondary" onClick={toggleChapterUpload}>
              <Icon name="upload" />
              {t('Import')}
            </Button>
            <Button onClick={handleCreateBookClick}>
              <Icon name="plus" />
              {t('Create')}
            </Button>
          </DashboardActions>
        </div>
      </div>
      <div>
        <div className="dashboard-tabs" role="tablist" aria-label={t('Books')}>
          {tabLabels.map((label, index) => (
            <button
              key={index}
              id={`dashboard-tab-${index}`}
              type="button"
              role="tab"
              className="dashboard-tab"
              aria-selected={activeTab === index}
              aria-controls={`dashboard-panel-${index}`}
              tabIndex={activeTab === index ? 0 : -1}
              onClick={() => setActiveTab(index)}
              onKeyDown={event => onTabKeyDown(event, index)}
            >
              {label}
            </button>
          ))}
        </div>
        <div
          role="tabpanel"
          id={`dashboard-panel-${activeTab}`}
          aria-labelledby={`dashboard-tab-${activeTab}`}
        >
          {activeTab === 0 && (
            <div className="book-list" aria-busy={isFetchingWritingList}>
              {renderBooksList({books: writing, showUpload: true, isLoading: isFetchingWritingList})}
            </div>
          )}
          {activeTab === 1 && (
            <div className="book-list" aria-busy={isFetchingCollaborationsList}>
              {renderBooksList({books: collaborating, isLoading: isFetchingCollaborationsList})}
            </div>
          )}
          {activeTab === 2 && <SampleChaptersTab />}
        </div>
        {userProfile?.account?.product?.internalId === 'free' && [
          <GetMoreReadersMessage
            key="get-more-readers"
            user={user}
            userProfile={userProfile}
          />,
          <Link
            key="check-bonus"
            style={{ float: 'right' }}
            to="/account/reader-bonus"
          >
            <em>{t('CheckBonus')}</em>
          </Link>,
        ]}
      </div>
      {showChapterUpload && (
        <ChapterUploadModal
          createBook
          open
          onClose={toggleChapterUpload}
          toggleOpen={toggleChapterUpload}
        />
      )}
      {showModal === 'create-book' && (
        <Dialog
          open
          title={t('CreateManuscript')}
          onClose={hideModal}
          actions={<Button variant="secondary" onClick={hideModal}>{t('Close')}</Button>}
        >
          <NewBook />
        </Dialog>
      )}
    </Dashboard>
  );
};

export default MyManuscripts;
